import { useState, useEffect } from "react";
import {
  Star,
  Search,
  Package,
  Calendar,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  CheckCircle,
  Clock,
  Image as ImageIcon,
  Plus,
  X,
  Filter,
  ThumbsUp,
  ThumbsDown,
  ShieldCheck,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { useToast } from "../context/ToastContext";
import BaseApi from "../services/base-api";
import LoadingComponent from "../components/LoadingComponent";
import { LocalizedText } from "../types/LocalizedText";

interface MyRating {
  id: string;
  userId: string;
  productId: string;
  orderId: string;
  rating: number;
  review: string | null;
  title: string | null;
  images: string[];
  pros: string | null;
  cons: string | null;
  isVerifiedPurchase: boolean;
  isApproved: boolean;
  isRecommended: boolean | null;
  helpfulCount: number;
  notHelpfulCount: number;
  adminResponse: string | null;
  adminRespondedAt: string | null;
  adminRespondedBy: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  product: {
    id: string;
    name?: LocalizedText;
    sku: string;
    images: string[];
  };
  order: {
    id: string;
    orderNumber: string;
    deliveredAt: string;
  };
}

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface ApiResponse {
  success: boolean;
  data: MyRating[];
  pagination: PaginationData;
}

type FilterStatus = "all" | "pending" | "approved";

const MyRatingsPage = () => {
  const { t, i18n } = useTranslation();
  const { success, err } = useToast();
  const currentLang = i18n.language as "en" | "ar";

  const [ratings, setRatings] = useState<MyRating[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [selectedRating, setSelectedRating] = useState<MyRating | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>("");

  // Edit form states
  const [editRating, setEditRating] = useState(5);
  const [editTitle, setEditTitle] = useState("");
  const [editReview, setEditReview] = useState("");
  const [editPros, setEditPros] = useState<string[]>([""]);
  const [editCons, setEditCons] = useState<string[]>([""]);
  const [editImages, setEditImages] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState<PaginationData>({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });

  useEffect(() => {
    loadMyRatings();
  }, [pagination.currentPage, filterStatus]);

  const loadMyRatings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.currentPage.toString(),
        limit: pagination.itemsPerPage.toString(),
      });

      if (filterStatus !== "all") {
        params.append(
          "isApproved",
          filterStatus === "approved" ? "true" : "false",
        );
      }

      const res: ApiResponse = await BaseApi.get(
        `/ratings/my-ratings?${params.toString()}`,
      );

      if (res.success && res.data) {
        setRatings(res.data);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      }
    } catch (error) {
      console.error("Error loading ratings:", error);
      err(t("Failed to load your ratings"));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rating: MyRating) => {
    setSelectedRating(rating);
    setEditRating(rating.rating);
    setEditTitle(rating.title || "");
    setEditReview(rating.review || "");
    setEditPros(rating.pros?.split("\n").filter(Boolean) || [""]);
    setEditCons(rating.cons?.split("\n").filter(Boolean) || [""]);
    setEditImages([]);
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!selectedRating) return;

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("rating", editRating.toString());

      if (editTitle.trim()) {
        formData.append("title", editTitle.trim());
      }

      if (editReview.trim()) {
        formData.append("review", editReview.trim());
      }

      const prosText = editPros.filter((p) => p.trim()).join("\n");
      if (prosText) {
        formData.append("pros", prosText);
      }

      const consText = editCons.filter((c) => c.trim()).join("\n");
      if (consText) {
        formData.append("cons", consText);
      }

      editImages.forEach((img) => {
        formData.append("images", img);
      });

      const res = await BaseApi.put(`/ratings/${selectedRating.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.success) {
        success(t("Rating updated successfully!"));
        setShowEditModal(false);
        setSelectedRating(null);
        loadMyRatings();
      }
    } catch (error: any) {
      console.error("Error updating rating:", error);
      err(error.message || t("Failed to update rating"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (ratingId: string) => {
    if (!window.confirm(t("Are you sure you want to delete this rating?"))) {
      return;
    }

    try {
      const res = await BaseApi.delete(`/ratings/${ratingId}`);
      if (res.success) {
        success(t("Rating deleted successfully"));
        loadMyRatings();
      }
    } catch (error) {
      console.error("Error deleting rating:", error);
      err(t("Failed to delete rating"));
    }
  };

  const addProField = () => {
    setEditPros([...editPros, ""]);
  };

  const removeProField = (index: number) => {
    if (editPros.length > 1) {
      setEditPros(editPros.filter((_, i) => i !== index));
    }
  };

  const updateProField = (index: number, value: string) => {
    const newPros = [...editPros];
    newPros[index] = value;
    setEditPros(newPros);
  };

  const addConField = () => {
    setEditCons([...editCons, ""]);
  };

  const removeConField = (index: number) => {
    if (editCons.length > 1) {
      setEditCons(editCons.filter((_, i) => i !== index));
    }
  };

  const updateConField = (index: number, value: string) => {
    const newCons = [...editCons];
    newCons[index] = value;
    setEditCons(newCons);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (editImages.length + files.length > 5) {
      err(t("Maximum 5 images allowed"));
      return;
    }
    setEditImages([...editImages, ...files]);
  };

  const removeImage = (index: number) => {
    setEditImages(editImages.filter((_, i) => i !== index));
  };

  const filteredRatings = ratings.filter((rating) => {
    const productName =
      rating?.product?.name?.[currentLang]?.toLowerCase() || "";
    const matchesSearch = productName.includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(currentLang, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getProductName = (name?: LocalizedText) => {
    return name?.[currentLang] || name?.en;
  };

  if (loading) {
    return <LoadingComponent title={t("Ratings")} />;
  }

  return (
    <div className="min-h-screen bg-[#f2e6db] py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <Star className="w-10 h-10 text-yellow-500" />
            {t("My Ratings & Reviews")}
          </h1>
          <p className="text-gray-600">
            {t("Manage your product reviews and ratings")}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">
                  {t("Total Reviews")}
                </p>
                <p className="text-3xl font-bold text-gray-900">
                  {pagination.totalItems}
                </p>
              </div>
              <div className="p-4 bg-yellow-100 rounded-xl">
                <MessageSquare className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">
                  {t("Approved")}
                </p>
                <p className="text-3xl font-bold text-gray-900">
                  {ratings.filter((r) => r.isApproved).length}
                </p>
              </div>
              <div className="p-4 bg-green-100 rounded-xl">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">
                  {t("Pending")}
                </p>
                <p className="text-3xl font-bold text-gray-900">
                  {ratings.filter((r) => !r.isApproved).length}
                </p>
              </div>
              <div className="p-4 bg-orange-100 rounded-xl">
                <Clock className="w-8 h-8 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t("Search by product name...")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:outline-none transition-colors"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={filterStatus}
                onChange={(e) =>
                  setFilterStatus(e.target.value as FilterStatus)
                }
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:outline-none transition-colors appearance-none bg-white cursor-pointer"
              >
                <option value="all">{t("All Reviews")}</option>
                <option value="approved">{t("Approved")}</option>
                <option value="pending">{t("Pending")}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Ratings List */}
        {filteredRatings.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <Package className="w-20 h-20 text-gray-300 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {t("No ratings found")}
            </h3>
            <p className="text-gray-600">
              {searchQuery
                ? t("Try adjusting your search criteria")
                : t("You haven't reviewed any products yet")}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredRatings.map((rating) => (
              <div
                key={rating.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                    <div className="flex gap-4">
                      {/* Product Image */}
                      <div className="flex-shrink-0">
                        <img
                          src={rating.product.images[0]}
                          alt={getProductName(rating?.product?.name)}
                          className="w-24 h-24 object-cover rounded-xl border-2 border-gray-200"
                        />
                      </div>

                      {/* Product Info */}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {getProductName(rating.product.name)}
                        </h3>
                        <div className="flex items-center gap-2 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-5 h-5 ${
                                i < rating.rating
                                  ? "text-yellow-400 fill-yellow-400"
                                  : "text-gray-300"
                              }`}
                            />
                          ))}
                          <span className="text-sm font-semibold text-gray-700">
                            {rating.rating}/5
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(rating.createdAt)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Package className="w-4 h-4" />
                            <span>{rating.order.orderNumber}</span>
                          </div>
                          {rating.isVerifiedPurchase && (
                            <div className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded-lg">
                              <ShieldCheck className="w-4 h-4" />
                              <span className="font-medium">
                                {t("Verified Purchase")}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Status & Actions */}
                    <div className="flex flex-col items-end gap-2">
                      <div
                        className={`px-4 py-2 rounded-xl font-semibold text-sm ${
                          rating.isApproved
                            ? "bg-green-100 text-green-700"
                            : "bg-orange-100 text-orange-700"
                        }`}
                      >
                        {rating.isApproved ? t("Approved") : t("Pending")}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(rating)}
                          className="p-2 text-blue-600 hover:bg-[#0d223f] rounded-lg transition-colors"
                          title={t("Edit")}
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(rating.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title={t("Delete")}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Review Content */}
                  <div className="space-y-4">
                    {rating.title && (
                      <h4 className="text-lg font-bold text-gray-900">
                        {rating.title}
                      </h4>
                    )}

                    {rating.review && (
                      <p className="text-gray-700 leading-relaxed">
                        {rating.review}
                      </p>
                    )}

                    {/* Pros and Cons */}
                    {(rating.pros || rating.cons) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        {rating.pros && (
                          <div className="bg-green-50 rounded-xl p-4">
                            <h5 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                              <ThumbsUp className="w-4 h-4" />
                              {t("Pros")}
                            </h5>
                            <ul className="space-y-1">
                              {rating.pros.split("\n").map((pro, idx) => (
                                <li
                                  key={idx}
                                  className="text-green-800 text-sm flex items-start gap-2"
                                >
                                  <span className="text-green-600 mt-1">•</span>
                                  <span>{pro}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {rating.cons && (
                          <div className="bg-red-50 rounded-xl p-4">
                            <h5 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                              <ThumbsDown className="w-4 h-4" />
                              {t("Cons")}
                            </h5>
                            <ul className="space-y-1">
                              {rating.cons.split("\n").map((con, idx) => (
                                <li
                                  key={idx}
                                  className="text-red-800 text-sm flex items-start gap-2"
                                >
                                  <span className="text-red-600 mt-1">•</span>
                                  <span>{con}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Images */}
                    {rating.images && rating.images.length > 0 && (
                      <div className="flex gap-3 flex-wrap">
                        {rating.images.map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`Review ${idx + 1}`}
                            onClick={() => {
                              setSelectedImage(img);
                              setShowImageModal(true);
                            }}
                            className="w-24 h-24 object-cover rounded-lg cursor-pointer hover:ring-2 hover:ring-yellow-500 transition-all"
                          />
                        ))}
                      </div>
                    )}

                    {/* Admin Response */}
                    {rating.adminResponse && (
                      <div className="bg-blue-100 border-l-4 border-blue-400 rounded-lg p-4 mt-4">
                        <div className="flex items-start gap-3">
                          <MessageSquare className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                          <div>
                            <h5 className="font-semibold text-blue-900 mb-1">
                              {t("Admin Response")}
                            </h5>
                            <p className="text-blue-800 text-sm">
                              {rating.adminResponse}
                            </p>
                            {rating.adminRespondedAt && (
                              <p className="text-blue-600 text-xs mt-2">
                                {formatDate(rating.adminRespondedAt)}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Helpfulness */}
                    <div className="flex items-center gap-4 pt-4 border-t border-gray-600">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <ThumbsUp className="w-4 h-4" />
                        <span className="font-medium">
                          {rating.helpfulCount}
                        </span>
                        <span>{t("helpful")}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <ThumbsDown className="w-4 h-4" />
                        <span className="font-medium">
                          {rating.notHelpfulCount}
                        </span>
                        <span>{t("not helpful")}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  currentPage: prev.currentPage - 1,
                }))
              }
              disabled={!pagination.hasPrevPage}
              className="flex items-center gap-2 px-6 py-3 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
            >
              <ChevronLeft className="w-5 h-5" />
              {t("Previous")}
            </button>

            <div className="flex items-center gap-2">
              {[...Array(pagination.totalPages)].map((_, idx) => (
                <button
                  key={idx}
                  onClick={() =>
                    setPagination((prev) => ({ ...prev, currentPage: idx + 1 }))
                  }
                  className={`w-10 h-10 rounded-xl font-semibold transition-all ${
                    pagination.currentPage === idx + 1
                      ? "bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-lg"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {idx + 1}
                </button>
              ))}
            </div>

            <button
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  currentPage: prev.currentPage + 1,
                }))
              }
              disabled={!pagination.hasNextPage}
              className="flex items-center gap-2 px-6 py-3 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
            >
              {t("Next")}
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedRating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2">
                    {t("Edit Your Review")}
                  </h2>
                  <p className="text-yellow-100 text-sm">
                    {getProductName(selectedRating.product.name)}
                  </p>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Rating */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Your Rating")} <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setEditRating(value)}
                      className="transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-10 h-10 ${
                          value <= editRating
                            ? "text-yellow-400 fill-yellow-400"
                            : "text-gray-300"
                        }`}
                      />
                    </button>
                  ))}
                  <span className="ml-2 text-lg font-semibold text-gray-700">
                    {editRating}/5
                  </span>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Review Title")}
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder={t("Sum up your experience")}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-yellow-500 focus:outline-none text-sm"
                  maxLength={100}
                />
              </div>

              {/* Review */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Your Review")}
                </label>
                <textarea
                  value={editReview}
                  onChange={(e) => setEditReview(e.target.value)}
                  rows={5}
                  placeholder={t("Tell us about your experience...")}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-yellow-500 focus:outline-none resize-none text-sm"
                  maxLength={1000}
                />
              </div>

              {/* Pros */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Pros")}
                </label>
                <div className="space-y-2">
                  {editPros.map((pro, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={pro}
                        onChange={(e) => updateProField(index, e.target.value)}
                        placeholder={t("What did you like?")}
                        className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none text-sm"
                      />
                      {editPros.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeProField(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addProField}
                    className="flex items-center gap-2 text-sm text-green-600 hover:text-green-700 font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    {t("Add another pro")}
                  </button>
                </div>
              </div>

              {/* Cons */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Cons")}
                </label>
                <div className="space-y-2">
                  {editCons.map((con, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={con}
                        onChange={(e) => updateConField(index, e.target.value)}
                        placeholder={t("What could be better?")}
                        className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none text-sm"
                      />
                      {editCons.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeConField(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addConField}
                    className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    {t("Add another con")}
                  </button>
                </div>
              </div>

              {/* Images */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Photos")} ({editImages.length}/5)
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {editImages.map((img, index) => (
                    <div key={index} className="relative">
                      <img
                        src={URL.createObjectURL(img)}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {editImages.length < 5 && (
                    <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-yellow-500 transition-colors">
                      <ImageIcon className="w-6 h-6 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-500">
                        {t("Add Photo")}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={() => setShowEditModal(false)}
                  disabled={submitting}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold disabled:opacity-50"
                >
                  {t("Cancel")}
                </button>
                <button
                  onClick={handleUpdate}
                  disabled={submitting}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl hover:shadow-lg transition-all font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t("Updating...")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      {t("Update Review")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setShowImageModal(false)}
        >
          <button
            onClick={() => setShowImageModal(false)}
            className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={selectedImage}
            alt="Review"
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>
      )}
    </div>
  );
};

export default MyRatingsPage;
